/* ============================================================================
  Collection Video Lazy Loading - Performance Optimization
  Implements intersection observer for video loading to improve initial page speed
============================================================================== */

class VideoLazyLoader {
  constructor() {
    this.observer = null;
    this.loadedVideos = new Set();
    this.init();
  }

  init() {
    // Check if intersection observer is supported
    if (!('IntersectionObserver' in window)) {
      // Fallback: load all videos immediately for older browsers
      this.loadAllVideos();
      return;
    }

    this.setupObserver();
    this.observeVideos();
  }

  setupObserver() {
    const options = {
      root: null, // viewport
      rootMargin: '50px 0px', // Load when 50px away from viewport
      threshold: 0.1 // Trigger when 10% visible
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadVideo(entry.target);
          this.observer.unobserve(entry.target);
        }
      });
    }, options);
  }

  observeVideos() {
    const lazyVideoContainers = document.querySelectorAll('[data-lazy-video]');
    lazyVideoContainers.forEach(container => {
      this.observer.observe(container);
    });

    // Also handle media card videos that are directly rendered
    this.playMediaCardVideos();
  }

  playMediaCardVideos() {
    const mediaCardVideos = document.querySelectorAll('[data-media-card] video');
    mediaCardVideos.forEach(video => {
      // Simple: just try to play
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.log('Autoplay prevented - user interaction required');
        });
      }
    });
  }

  loadVideo(container) {
    const videoElement = container.querySelector('video[data-video-src]');
    const placeholderElement = container.querySelector('.lazy-video-placeholder');
    const posterImage = container.querySelector('.collection-media-card__video-poster');

    if (videoElement) {
      this.loadDirectVideo(videoElement, posterImage);
    } else if (placeholderElement) {
      this.loadShopifyVideo(placeholderElement, container);
    }
  }

  loadDirectVideo(videoElement, posterImage) {
    const videoSrc = videoElement.dataset.videoSrc;
    if (!videoSrc || this.loadedVideos.has(videoSrc)) return;

    const container = videoElement.closest('[data-lazy-video]');

    // Create source element
    const source = document.createElement('source');
    source.src = videoSrc;
    source.type = 'video/mp4';
    videoElement.appendChild(source);

    // Add autoplay after loading
    videoElement.autoplay = true;

    // Mark as loaded
    this.loadedVideos.add(videoSrc);
    videoElement.removeAttribute('data-video-src');

    // Load the video
    videoElement.load();

    // Add loading state management
    videoElement.addEventListener('loadstart', () => {
      if (container) container.classList.add('video-loading');
    });

    videoElement.addEventListener('canplay', () => {
      if (container) {
        container.classList.remove('video-loading');
        container.classList.add('video-loaded');
      }
      // Hide poster image when video is ready to play
      if (posterImage) {
        posterImage.style.opacity = '0';
        posterImage.style.transition = 'opacity 0.3s ease';
        setTimeout(() => {
          posterImage.style.display = 'none';
        }, 300);
      }
      videoElement.play().catch(e => {
        console.warn('Video autoplay failed:', e);
      });
    });

    videoElement.addEventListener('error', () => {
      if (container) {
        container.classList.remove('video-loading');
        container.classList.add('video-error');
      }
      console.warn('Failed to load video:', videoSrc);
    });
  }

  loadShopifyVideo(placeholderElement, container) {
    try {
      const videoSources = JSON.parse(placeholderElement.dataset.videoSources);
      if (!videoSources || !videoSources.length) return;

      // Create video element for Shopify video
      const videoElement = document.createElement('video');
      videoElement.className = 'collection-media-card__video-element';
      videoElement.muted = true;
      videoElement.loop = true;
      videoElement.autoplay = true;
      videoElement.playsInline = true;
      videoElement.controls = false;

      // Add sources
      videoSources.forEach(source => {
        const sourceElement = document.createElement('source');
        sourceElement.src = source.url;
        sourceElement.type = source.mime_type || 'video/mp4';
        videoElement.appendChild(sourceElement);
      });

      // Replace placeholder with video
      container.replaceChild(videoElement, placeholderElement);

      // Add loading state management
      videoElement.addEventListener('loadstart', () => {
        container.classList.add('video-loading');
      });

      videoElement.addEventListener('canplay', () => {
        container.classList.remove('video-loading');
        container.classList.add('video-loaded');
      });

      videoElement.addEventListener('error', () => {
        container.classList.remove('video-loading');
        container.classList.add('video-error');
        console.warn('Failed to load Shopify video');
      });

    } catch (e) {
      console.warn('Failed to parse video sources:', e);
      container.classList.add('video-error');
    }
  }

  loadAllVideos() {
    // Fallback for browsers without intersection observer
    const lazyVideoContainers = document.querySelectorAll('[data-lazy-video]');
    lazyVideoContainers.forEach(container => {
      this.loadVideo(container);
    });
  }

  // Public method to manually load a video (for programmatic control)
  forceLoadVideo(container) {
    if (this.observer) {
      this.observer.unobserve(container);
    }
    this.loadVideo(container);
  }

  // Cleanup method
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.loadedVideos.clear();
  }
}

// Auto-initialize when DOM is ready
function initVideoLazyLoader() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.videoLazyLoader = new VideoLazyLoader();
    });
  } else {
    window.videoLazyLoader = new VideoLazyLoader();
  }
}

// Initialize on script load
initVideoLazyLoader();

// Re-initialize when new content is loaded (for infinite scroll/AJAX)
window.reinitVideoLazyLoader = function() {
  if (window.videoLazyLoader) {
    window.videoLazyLoader.observeVideos();
    window.videoLazyLoader.playMediaCardVideos();
  }
};

// Integration with existing collection systems
document.addEventListener('collectionUpdated', function() {
  window.reinitVideoLazyLoader();
});

// Hook into load more functionality
document.addEventListener('click', function(e) {
  if (e.target.closest('[data-load-more-trigger]')) {
    // Small delay to allow new content to be inserted
    setTimeout(() => {
      window.reinitVideoLazyLoader();
    }, 100);
  }
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = VideoLazyLoader;
}